import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
    TextField,
    Button,
    Grid,
    Typography,
    Paper,
    Select,
    MenuItem,
    FormLabel,
} from '@mui/material';
import { apiAbsence } from '../../services/absence/Absence';
import { apiAbsenceDelete } from '../../services/absenceDelete/AbsenceDelete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import './absenceComponent.css';

const ABSENCE_TYPE_OPTIONS = [
    { id: '2', name: 'Urlop wypoczynkowy' },
    { id: '10', name: 'Urlop na żądanie' },
    { id: '6', name: 'Dzień na dziecko art 188' },
    { id: '18', name: 'Delegacja' },
    { id: '30', name: 'Praca zdalna' },
    { id: '32', name: 'Urlop z powodu siły wyższej' },
    { id: '35', name: 'Praca zdalna okazjonalna' },
    { id: '36', name: 'Urlop okolicznościowy' },
    { id: '3', name: 'Urlop bezpłatny' },
];

const formatDate = (date) => {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
};

const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);

    return result;
};

const getTomorrowDate = () => formatDate(addDays(new Date(), 1));

const formatHoursForInput = (value) => {
    if (value === null || value === undefined || value === '') return '';

    const textValue = String(value);
    if (/^\d{1,2}:\d{2}$/.test(textValue)) return textValue;

    const numericValue = Number(textValue.replace(',', '.'));
    if (Number.isNaN(numericValue)) return '';

    const totalMinutes = Math.round(numericValue * 60);
    const formattedHours = Math.floor(totalMinutes / 60);
    const formattedMinutes = String(totalMinutes % 60).padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
};

const getHoursDigits = (value) => {
    return value.replace(/\D/g, '');
};

const sanitizeHoursInput = (value) => {
    return getHoursDigits(value).slice(0, 4);
};

const formatHoursDuringInput = (value) => {
    const textValue = String(value ?? '');
    const colonIndex = textValue.indexOf(':');

    if (colonIndex >= 0) {
        const hours = sanitizeHoursInput(textValue.slice(0, colonIndex)).slice(
            0,
            2
        );
        const minutes = sanitizeHoursInput(textValue.slice(colonIndex + 1)).slice(
            0,
            2
        );

        return `${hours}:${minutes}`;
    }

    const digits = sanitizeHoursInput(textValue);

    if (digits.length < 2) return digits;
    if (digits.length === 2) return `${digits}:`;

    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const isAllowedHoursInput = (value) => {
    return getHoursDigits(value).length <= 4;
};

const parseHoursInput = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const textValue = String(value);
    const compactTimeMatch = textValue.match(/^(\d{3,4})$/);
    const match =
        compactTimeMatch ||
        textValue.match(/^(\d{1,2})(?::(\d{0,2}))?$/);
    if (!match) return null;

    const parsedHours = compactTimeMatch
        ? Number(textValue.slice(0, -2))
        : Number(match[1]);
    const parsedMinutes = compactTimeMatch
        ? Number(textValue.slice(-2))
        : match[2]
          ? Number(match[2])
          : 0;

    if (
        Number.isNaN(parsedHours) ||
        Number.isNaN(parsedMinutes) ||
        parsedMinutes > 59 ||
        parsedHours > 24 ||
        (parsedHours === 24 && parsedMinutes > 0)
    ) {
        return null;
    }

    return Math.round((parsedHours + parsedMinutes / 60) * 10000) / 10000;
};

const AbsenceComponent = (props) => {
    const [id, setId] = useState(-1);
    const isAuthenticated =
        localStorage.getItem('token') !== null &&
        localStorage.getItem('token') !== '';
    const [startDate, setStartDate] = useState(new Date()); // Default to today's date
    const [endDate, setEndDate] = useState(new Date());
    const [hours, setHours] = useState(null);
    const [absenceType, setAbsenceType] = useState('');
    const [absenceName, setAbsenceName] = useState('');
    const [notVisibleAbsenceTypeIds, setNotVisibleAbsenceTypeIds] = useState([]);
    const [description, setDescription] = useState('');
    const [documentNumber, setDocumentNumber] = useState('');
    const [occasionalLeaveDetailsEnabled, setOccasionalLeaveDetailsEnabled] =
        useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingBefore, setIsLoadingBefore] = useState(true);
    const [loadData, setLoadData] = useState(false);
    const [isLoadingDelete, setIsLoadingDelete] = useState(false);
    const [isRejected, setIsRejected] = useState(false);
    let location = useLocation();
    const [searchParams] = useSearchParams();
    const [openDelete, setOpenDelete] = useState(false);
    const [reasonMaxLength, setReasonMaxLength] = useState(100);

    const handleStartDateChange = (e) => {
        const selectedDate = e.target.value;
        const nextStartDate =
            (Number(absenceType) === 2 || Number(absenceType) === 32)  && selectedDate < getTomorrowDate()
                ? getTomorrowDate()
                : selectedDate;

        setValidationMessage('');
        setStartDate(nextStartDate);
        if (id <= 0) setEndDate(nextStartDate);
    };

    const handleEndDateChange = (e) => {
        setValidationMessage('');
        setEndDate(e.target.value);
        setHours('');
    };

    const handleHoursChange = (e) => {
        setHours(formatHoursDuringInput(e.target.value));
    };

    const handleHoursKeyDown = (e) => {
        const navigationKeys = [
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
            'Tab',
        ];

        if (
            navigationKeys.includes(e.key) ||
            ((e.ctrlKey || e.metaKey) &&
                ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
        ) {
            return;
        }

        if (!/^[\d:]$/.test(e.key)) {
            e.preventDefault();
            return;
        }

        const input = e.target;
        const currentValue = input.value ?? '';
        const selectionStart = input.selectionStart ?? currentValue.length;
        const selectionEnd = input.selectionEnd ?? currentValue.length;
        const nextValue =
            currentValue.slice(0, selectionStart) +
            e.key +
            currentValue.slice(selectionEnd);

        if (!isAllowedHoursInput(nextValue)) {
            e.preventDefault();
        }
    };

    const handleHoursPaste = (e) => {
        e.preventDefault();

        const input = e.target;
        const currentValue = input.value ?? '';
        const selectionStart = input.selectionStart ?? currentValue.length;
        const selectionEnd = input.selectionEnd ?? currentValue.length;
        const pastedValue = e.clipboardData.getData('text');
        const nextValue =
            currentValue.slice(0, selectionStart) +
            pastedValue +
            currentValue.slice(selectionEnd);

        setHours(formatHoursDuringInput(nextValue));
    };

    const handleHoursBlur = () => {
        const parsedHours = parseHoursInput(hours);
        if (hours && parsedHours !== null) {
            setHours(formatHoursForInput(parsedHours));
        }
    };

    const handleClose = () => {
        setOpenDelete(false);
    };

    const handleAbsenceTypeChange = (event) => {
        const selectedAbsenceType = event.target.value;
        setValidationMessage('');
        setAbsenceType(selectedAbsenceType);

        if (Number(selectedAbsenceType) === 2) {
            const tomorrow = getTomorrowDate();
            setStartDate(tomorrow);
            if (id <= 0) setEndDate(tomorrow);
            setHours('');
        } else if (Number(selectedAbsenceType) === 10 || Number(selectedAbsenceType) === 32) {
            const today = formatDate(new Date());
            setStartDate(today);
            if (id <= 0) setEndDate(today);
            setHours('');
        }
    };

    const handleDescriptionChange = (event) => {
        let description = event.target.value;

        if (description) setReasonMaxLength(100 - description.length);
        else setReasonMaxLength(100);

        setDescription(description);
    };

    const removeAbsence = async () => {
        setIsLoadingDelete(true);
        const absenceDelete = await apiAbsenceDelete.post({ id: id });
        if (absenceDelete) {
            setIsLoadingDelete(false);
            setOpenDelete(false);
            window.location.href = '/absenceList';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const selectedStart = new Date(startDate);
        selectedStart.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedStart < today) {
            setValidationMessage('Nieobecność nie może rozpocząć się wcześniej niż dzisiaj');
            return;
        }

        if (endDate < startDate) {
            setValidationMessage('Data od nie może być większa niż data do');
            return;
        }

        if (Number(absenceType) === 2 && startDate < getTomorrowDate()) {
            setValidationMessage('Urlop wypoczynkowy moze rozpoczac sie najwczesniej jutro');
            return;
        }

        const isHolidayLeave = Number(absenceType) === 2;
        const hoursToSave = isHolidayLeave ? null : parseHoursInput(hours);

        if (!isHolidayLeave && hours && hoursToSave === null) {
            setValidationMessage('Podaj godziny w formacie GG:MM');
            return;
        }

        if (hoursToSave && hoursToSave > 24) {
            setValidationMessage('Liczba godzin nie może być większa niż 24');
            return;
        }

        if (!absenceType) {
            setValidationMessage('Pole rodzaj nieobecności nie może być puste');
            return;
        }

        if (!isLoading) {
            setIsLoading(true);
            let result = await apiAbsence.post({
                dateFrom: startDate,
                dateTo: endDate,
                description: description,
                documentNumber: documentNumber,
                absenceType: absenceType,
                hours: hoursToSave,
                id: id,
            });

            if (result.error) {
                setIsLoading(false);
                setValidationMessage(result.error);
            } else {
                setIsLoading(false);
                window.location.href = '/absenceList';
            }
        }
    };

    useEffect(() => {
        if (!isAuthenticated) window.location.href = '/login';
        if (location.search.indexOf('id=') >= 0) {
            let id = searchParams.get('id');
            setId(id);
            if (!loadData) {
                setIsLoadingBefore(true);
                setLoadData(true);
                fetchData(id);
            }
        } else {
            const tomorrow = getTomorrowDate();
            setStartDate(tomorrow);
            setEndDate(tomorrow);
            setAbsenceType(2);
            if (!loadData) {
                setLoadData(true);
                fetchData(0);
            }
        }
        async function fetchData(id) {
            try {
                let absence = await apiAbsence.getSingle(id);
                if (absence != null) {
                    const hiddenTypeIds =
                        absence.notVisibleAbsenceTypeIds ?? [];
                    setNotVisibleAbsenceTypeIds(hiddenTypeIds);
                    setOccasionalLeaveDetailsEnabled(
                        absence.occasionalLeaveDetailsEnabled === true
                    );
                    if (Number(id) <= 0) {
                        const firstVisibleType = ABSENCE_TYPE_OPTIONS.find(
                            (option) =>
                                !hiddenTypeIds.includes(Number(option.id))
                        );
                        setAbsenceType(firstVisibleType?.id ?? '');
                        setIsLoadingBefore(false);
                        return;
                    }
                    setStartDate(absence.dateFrom);
                    setEndDate(absence.dateTo);
                    setHours(formatHoursForInput(absence.hours));
                    setAbsenceType(absence.absenceId);
                    setAbsenceName(absence.absenceName);
                    setDescription(absence.description);
                    setDocumentNumber(absence.documentNumber ?? '');
                    setIsRejected(absence.isAccepted || absence.isRejected);
                    if (absence.isAccepted || absence.isRejected)
                        setAbsenceType(-1);
                    if (absence.description)
                        setReasonMaxLength(100 - absence.description.length);
                    setIsLoadingBefore(false);
                }
            } catch (error) {
                console.error('Error fetching absence:', error);
                setIsLoadingBefore(false);
            }
        }
    }, [isAuthenticated, loadData, location.search, searchParams]); // Run only on component mount

    return (
        <div className="absence-container absence-form-page">
            {isLoadingBefore && (
                <Grid container xs={12}>
                    <Grid item xs={12}>
                        <br />
                    </Grid>
                    <Grid item xs={12} className="other">
                        <span className="loader-black"></span>
                    </Grid>
                </Grid>
            )}
            {!isLoadingBefore && (
                <div className="padding-body-2">
                    <Paper elevation={3} variant="outlined" square>
                        <form onSubmit={handleSubmit}>
                            <br />
                            {id <= 0 && (
                                <Typography variant="h5" gutterBottom>
                                    Nowa nieobecność
                                </Typography>
                            )}
                            {id > 0 && !isRejected && (
                                <Typography variant="h5" gutterBottom>
                                    Edycja nieobecności
                                </Typography>
                            )}
                            {id > 0 && isRejected && (
                                <Typography variant="h5" gutterBottom>
                                    Podgląd nieobecności
                                </Typography>
                            )}
                            <p className="validation-message">
                                {validationMessage}
                            </p>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={6}>
                                    <TextField
                                        label="Data od"
                                        type="date"
                                        disabled={isRejected}
                                        InputLabelProps={{ shrink: true }} // Adjust label positioning
                                        value={startDate}
                                        InputProps={{
                                            inputProps: {
                                                lang: 'en-GB',
                                                min:
                                                    Number(absenceType) === 2
                                                        ? getTomorrowDate()
                                                        : formatDate(new Date()),
                                            },
                                        }}
                                        onChange={(e) => {
                                            handleStartDateChange(e);
                                        }}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={6} sm={6}>
                                    <TextField
                                        label="Data do"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={endDate}
                                        disabled={isRejected}
                                        InputProps={{
                                            inputProps: {
                                                lang: 'en-GB',
                                                min: startDate,
                                            },
                                        }}
                                        onChange={(e) => {
                                            handleEndDateChange(e);
                                        }}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                {startDate === endDate && Number(absenceType) !== 2 && Number(absenceType) !== 36 && Number(absenceType) !== 10 && (
                                    <Grid item xs={6} sm={6}>
                                        <TextField
                                            label="Godziny i minuty"
                                            InputLabelProps={{ shrink: true }}
                                            value={hours ?? ''}
                                            disabled={isRejected}
                                            placeholder="GG:MM"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9:]{1,5}',
                                                maxLength: 5,
                                            }}
                                            onChange={(e) => {
                                                handleHoursChange(e);
                                            }}
                                            onKeyDown={handleHoursKeyDown}
                                            onPaste={handleHoursPaste}
                                            onBlur={handleHoursBlur}
                                            fullWidth
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <FormLabel>Rodzaj nieobecności</FormLabel>
                                    {!isRejected && (
                                        <Select
                                            value={absenceType}
                                            onChange={handleAbsenceTypeChange}
                                            disabled={isRejected}
                                            fullWidth
                                            required
                                        >
                                            {ABSENCE_TYPE_OPTIONS.filter(
                                                (option) =>
                                                    !notVisibleAbsenceTypeIds.includes(
                                                        Number(option.id)
                                                    )
                                            ).map((option) => (
                                                <MenuItem
                                                    key={option.id}
                                                    value={option.id}
                                                >
                                                    {option.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                    {isRejected && (
                                        <Select
                                            value={absenceType}
                                            onChange={handleAbsenceTypeChange}
                                            disabled={isRejected}
                                            fullWidth
                                        >
                                            <MenuItem value="-1">
                                                {absenceName}
                                            </MenuItem>
                                        </Select>
                                    )}
                                </Grid>
                                {(absenceType === 18 || absenceType === 30) && (
                                    <Grid item xs={12}>
                                        <TextField
                                            label={'Powód (dopuszczalna liczba znaków ' + reasonMaxLength + ')'}
                                            multiline
                                            rows={4}
                                            disabled={isRejected}
                                            inputProps={{ maxLength: 100 }}
                                            value={description}
                                            onChange={handleDescriptionChange}
                                            fullWidth
                                        />
                                    </Grid>
                                )}
                                {occasionalLeaveDetailsEnabled &&
                                    Number(absenceType) === 36 && (
                                        <>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Przyczyna"
                                                    multiline
                                                    rows={4}
                                                    disabled={isRejected}
                                                    inputProps={{ maxLength: 100 }}
                                                    value={description}
                                                    onChange={handleDescriptionChange}
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Numer dokumentu"
                                                    disabled={isRejected}
                                                    inputProps={{ maxLength: 100 }}
                                                    value={documentNumber}
                                                    onChange={(event) =>
                                                        setDocumentNumber(
                                                            event.target.value
                                                        )
                                                    }
                                                    fullWidth
                                                />
                                            </Grid>
                                        </>
                                    )}
                                {!isRejected && (
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            size="large"
                                            className="buttonFilled margin-bottom-0"
                                        >
                                            {isLoading && (
                                                <span className="loader"></span>
                                            )}
                                            {!isLoading && (
                                                <span>Zapisz nieobecność</span>
                                            )}
                                        </Button>
                                    </Grid>
                                )}
                                {id > 0 && !isRejected && (
                                    <Grid item xs={12}>
                                        <a
                                            className="cancelButton"
                                            onClick={() => {
                                                setOpenDelete(true);
                                            }}
                                        >
                                            Usuń
                                        </a>
                                    </Grid>
                                )}
                                {!isRejected && (
                                    <Grid item xs={12}>
                                        <a
                                            href="/absenceList"
                                            className="cancelButton"
                                        >
                                            Anuluj
                                        </a>
                                    </Grid>
                                )}
                                {isRejected && (
                                    <Grid item xs={12}>
                                        <a
                                            href="/absenceList"
                                            className="cancelButton"
                                        >
                                            Wróć
                                        </a>
                                    </Grid>
                                )}
                            </Grid>
                        </form>
                    </Paper>
                </div>
            )}
            {!isLoadingBefore && (
                <Dialog
                    open={openDelete}
                    onClose={handleClose}
                    style={{ padding: '10px' }}
                >
                    <DialogContent>
                        <DialogContentText>
                            Czy na pewno chcesz usunąć nieobecność?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Grid
                            container
                            spacing={2}
                            className="right-side action-margin"
                        >
                            <Grid item xs={3}></Grid>
                            <Grid item xs={3}>
                                <Button
                                    className="button"
                                    onClick={() => {
                                        removeAbsence();
                                    }}
                                >
                                    {isLoading && (
                                        <span className="loader"></span>
                                    )}
                                    {!isLoading && <span>TAK</span>}
                                </Button>
                            </Grid>
                            <Grid item xs={3}>
                                <a
                                    className="cancelButton"
                                    onClick={() => {
                                        setOpenDelete(false);
                                    }}
                                >
                                    Anuluj
                                </a>
                            </Grid>
                            <Grid item xs={3}></Grid>
                        </Grid>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default AbsenceComponent;
